/**
 * 🎯 EXECUTIVE DASHBOARD PAGE
 * ════════════════════════════════════════════════════════════════════════
 * 
 * RESPONSABILIDAD:
 * Entry point del sistema ejecutivo con EDIT mode support.
 * 
 * EDIT MODE RULES (Executive Immutability):
 * ✅ Changes staged locally until Save (NO backend until commit)
 * ✅ Remove: Staged in draft, backend recalculates on Save
 * ✅ Duplicate: Backend returns ONLY new widget, no move existing
 * ✅ Save: Backend recalculates ALL, returns full state
 * ❌ NO immediate backend calls during editing
 * ❌ NO layout jumps while editing
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import type { ExecutiveDashboard } from '../types';
import ExecutiveDashboardGrid from '../components/ExecutiveDashboardGrid';
import ExecutiveHeader from '../components/ExecutiveHeader';
import EditModeToolbar from '../components/EditModeToolbar';
import { useEditMode } from '../hooks/useEditMode';
import { duplicateWidget as apiDuplicateWidget, removeWidgets as apiRemoveWidgets } from '../services/executive.service';
import '../styles/page.css';

interface ExecutiveDashboardPageProps {
  dashboard: ExecutiveDashboard;
  onDashboardUpdate?: (dashboard: ExecutiveDashboard) => void;
  onDuplicate?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
}

const ExecutiveDashboardPage: React.FC<ExecutiveDashboardPageProps> = ({ 
  dashboard,
  onDashboardUpdate,
  onDuplicate,
  onMove,
  onDelete
}) => {
  
  const [isSaving, setIsSaving] = useState(false);
  const [newWidgetId, setNewWidgetId] = useState<string | null>(null);
  
  const {
    isEditing,
    hasChanges,
    removedWidgetIds,
    currentDashboard,
    enterEditMode,
    cancelEdit,
    markWidgetForRemoval,
    addDuplicatedWidget
  } = useEditMode(dashboard);
  
  console.log('🎯 ExecutiveDashboardPage rendering:', {
    id: dashboard.id,
    mode: dashboard.presentation.mode,
    widgetCount: dashboard.widgets.length,
    isEditing,
    hasChanges
  });
  
  /**
   * Enter EDIT mode
   */
  const handleEdit = () => {
    enterEditMode();
    toast.success('Edit mode enabled. Make your changes.', {
      icon: '✏️',
      duration: 3000
    });
  };
  
  /**
   * Cancel editing - discard all changes
   */
  const handleCancel = () => {
    if (hasChanges) {
      if (!window.confirm('Discard all changes?')) {
        return;
      }
    }
    cancelEdit();
    setNewWidgetId(null);
    toast('Changes discarded', { icon: '↩️' });
  };
  
  /**
   * Remove widget (staged locally, not backend yet)
   */
  const handleRemoveWidget = (widgetId: string) => {
    markWidgetForRemoval(widgetId);
    toast.success('Widget marked for removal', {
      icon: '🗑️',
      duration: 2000
    });
  };
  
  /**
   * Duplicate widget
   * Calls backend to get new widget with layout
   * DOES NOT move existing widgets
   */
  const handleDuplicateWidget = async (widgetId: string) => {
    try {
      toast.loading('Duplicating widget...', { id: 'duplicate' });
      
      // Backend returns ONLY the new widget with its layout
      const result = await apiDuplicateWidget(dashboard.id, widgetId);
      
      // Add to draft (existing widgets stay in place)
      addDuplicatedWidget(result.widget);
      setNewWidgetId(result.widget.id);
      
      toast.success('Widget duplicated. Review placement before saving.', {
        id: 'duplicate',
        icon: '📋',
        duration: 4000
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to duplicate widget', {
        id: 'duplicate'
      });
    }
  };
  
  /**
   * Save all changes
   * Backend recalculates ALL layouts for optimal grid
   */
  const handleSave = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    
    try {
      toast.loading('Saving changes...', { id: 'save' });
      
      // Send removals to backend for full recalculation
      if (removedWidgetIds.length > 0) {
        const result = await apiRemoveWidgets(dashboard.id, removedWidgetIds);
        
        // Backend returns full recalculated dashboard
        if (onDashboardUpdate) {
          onDashboardUpdate(result.dashboard);
        }
        
        toast.success(
          `${removedWidgetIds.length} widget(s) removed. Layouts optimized.`,
          {
            id: 'save',
            icon: '✅',
            duration: 4000
          }
        );
      } else {
        toast.success('Changes saved', { id: 'save' });
      }
      
      // Exit EDIT mode
      cancelEdit();
      setNewWidgetId(null);
      
    } catch (error: any) {
      toast.error(error.message || 'Failed to save changes', {
        id: 'save'
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="executive-dashboard-page">
      
      {/* EDIT mode toolbar (only when editing) */}
      {isEditing && (
        <EditModeToolbar
          hasChanges={hasChanges}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      )}
      
      {/* Header with actions menu */}
      <ExecutiveHeader
        dashboard={currentDashboard}
        onEdit={isEditing ? undefined : handleEdit}
        onDuplicate={onDuplicate}
        onMove={onMove}
        onDelete={onDelete}
      />
      
      {/* Banners ejecutivos (si existen) */}
      {currentDashboard.banners && currentDashboard.banners.length > 0 && (
        <div className="executive-banners">
          {currentDashboard.banners.map(banner => (
            <div key={banner.id} className={`executive-banner executive-banner-${banner.type}`}>
              <span className="banner-icon">
                {banner.type === 'insight' && '💡'}
                {banner.type === 'warning' && '⚠️'}
                {banner.type === 'trend' && '📈'}
              </span>
              <span className="banner-message">{banner.message}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Grid con widgets */}
      <ExecutiveDashboardGrid 
        widgets={currentDashboard.widgets}
        isEditing={isEditing}
        newWidgetId={newWidgetId}
        onRemoveWidget={handleRemoveWidget}
        onDuplicateWidget={handleDuplicateWidget}
      />
      
    </div>
  );
};

export default ExecutiveDashboardPage;
